import React from "react";

import { css, cx } from "@emotion/css";
import { FieldValues, FieldPath, useWatch, useFormContext, type PathValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFormError } from "@/hooks/useFormError.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { FormInputError } from "@/uiKit/atoms/forms/FormInputError.tsx";
import { SwitchInput } from "@/uiKit/atoms/inputs/SwitchInput.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

export type BaseFormSwitchInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  name: TName;
  label?: string;
  reserveErrorSpace?: boolean;

  onChange?: (value: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  size?: "small" | "medium";
};

export type FormSwitchInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = AtomProps<
  BaseFormSwitchInputProps<TFieldValues, TName>,
  StyleClasses<"root" | "content" | "error" | "label">
>;

export const FormSwitchInput = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  id,
  name,
  label,
  reserveErrorSpace,
  onChange,
  className,
  classes,
  ...props
}: FormSwitchInputProps<TFieldValues, TName>): React.ReactElement => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const formContext = useFormContext<TFieldValues>();
  const formError = useFormError(name);
  const value = useWatch({ name }) ?? false;

  return (
    <div id={id} className={cx(styles.container, classes?.root, className)}>
      <div className={cx(styles.content, classes?.content)}>
        <SwitchInput
          id={suffixId(id, "switch")}
          value={value}
          onChange={(value, event) => {
            formContext.setValue(name, value as PathValue<TFieldValues, TName>, {
              shouldTouch: true,
              shouldValidate: true,
              shouldDirty: true,
            });
            onChange?.(value, event);
          }}
          {...props}
        />
        {!!label && (
          <Typography
            variant="body"
            className={cx({ [styles.label]: true, [styles.labelError]: formError.hasError }, classes?.label)}
          >
            {label}
          </Typography>
        )}
      </div>
      <FormInputError
        reserveSpace={reserveErrorSpace}
        errorMessage={formError.errorMessage ? t(formError.errorMessage) : undefined}
        className={cx(styles.error, classes?.error)}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    minWidth: 0,
  }),
  content: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: theme.spacing(6),
    overflow: "clip",
  }),
  error: css({}),
  label: css({
    flex: "0 1 auto",
    minWidth: 0,
    textWrap: "nowrap",
    overflow: "clip",
    textOverflow: "ellipsis",
  }),
  labelError: css({
    color: theme.palette.error.main,
  }),
});
